import React from "react";

interface Swatch {
	/** Tailwind utility class applied to the swatch, e.g. "bg-brand" */
	className: string;
	/** Token name to display */
	name: string;
	/** Source hex value the token maps to (for reference) */
	hex: string;
	/** Suggested usage */
	usage?: string;
}

interface SwatchGroup {
	title: string;
	description?: string;
	swatches: Swatch[];
}

const groups: SwatchGroup[] = [
	{
		title: "Brand",
		description: "Origo identity colors. Prefer the semantic tokens below for status/UI.",
		swatches: [
			{
				className: "bg-brand",
				name: "brand",
				hex: "#49BD79",
				usage: "Origo green — primary brand accent",
			},
			{
				className: "bg-action",
				name: "action",
				hex: "#219CD0",
				usage: "Action blue — links, secondary actions",
			},
		],
	},
	{
		title: "Semantic / status",
		description: "Use these for state — never inline hex.",
		swatches: [
			{
				className: "bg-primary",
				name: "primary",
				hex: "#49BD79",
				usage: "Default shadcn primary (mapped to brand)",
			},
			{ className: "bg-success", name: "success", hex: "#49BD79", usage: "Success state" },
			{
				className: "bg-success-muted",
				name: "success.muted",
				hex: "#bdecd1",
				usage: "Success background",
			},
			{ className: "bg-warning", name: "warning", hex: "#FF6B00", usage: "Warning state" },
			{ className: "bg-danger", name: "danger", hex: "#EB5757", usage: "Error / destructive" },
			{
				className: "bg-danger-muted",
				name: "danger.muted",
				hex: "#FFEBEB",
				usage: "Error background",
			},
			{ className: "bg-info-muted", name: "info.muted", hex: "#D3EBF6", usage: "Info background" },
		],
	},
	{
		title: "Neutrals (shadcn base)",
		description: "Surface, text and border tokens. Driven by the CSS variables in tailwind.css.",
		swatches: [
			{ className: "bg-background", name: "background", hex: "#FFFFFF", usage: "Page background" },
			{ className: "bg-secondary", name: "secondary", hex: "#F5F5F5", usage: "Subtle surface" },
			{ className: "bg-muted", name: "muted", hex: "#F5F5F5", usage: "Muted surface" },
			{ className: "bg-accent", name: "accent", hex: "#F5F5F5", usage: "Hover / accent surface" },
			{ className: "bg-border", name: "border", hex: "#E5E5E5", usage: "Borders" },
			{ className: "bg-input", name: "input", hex: "#D4D9E2", usage: "Input borders" },
			{ className: "bg-ring", name: "ring", hex: "#49BD79", usage: "Focus ring (brand)" },
		],
	},
];

const ColorTokensExample: React.FC = () => {
	return (
		<div className="space-y-10">
			<p className="text-sm text-muted-foreground">
				These are the canonical theme tokens. In new Tailwind components reference them as utilities
				(<code className="rounded bg-muted px-1">bg-brand</code>,{" "}
				<code className="rounded bg-muted px-1">text-danger</code>,{" "}
				<code className="rounded bg-muted px-1">border-input</code>) — opacity modifiers like{" "}
				<code className="rounded bg-muted px-1">bg-danger/10</code> work too. Avoid inline hex.
			</p>

			{groups.map((group) => (
				<section key={group.title} className="space-y-3">
					<div>
						<h3 className="text-lg font-semibold">{group.title}</h3>
						{group.description && (
							<p className="text-sm text-muted-foreground">{group.description}</p>
						)}
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
						{group.swatches.map((swatch) => (
							<div
								key={swatch.name}
								className="flex items-center gap-3 rounded-lg border border-border p-3"
							>
								<div
									className={`h-12 w-12 shrink-0 rounded-md border border-border ${swatch.className}`}
								/>
								<div className="min-w-0">
									<div className="flex items-baseline gap-2">
										<span className="font-medium">{swatch.name}</span>
										<span className="text-xs text-muted-foreground">{swatch.hex}</span>
									</div>
									<code className="text-xs text-muted-foreground">{swatch.className}</code>
									{swatch.usage && <p className="text-xs text-muted-foreground">{swatch.usage}</p>}
								</div>
							</div>
						))}
					</div>
				</section>
			))}
		</div>
	);
};

export default ColorTokensExample;
